"use client";

import { useTranslations } from "next-intl";
import { ExternalLink, Palette } from "lucide-react";
import { motion } from "framer-motion";
import { Section } from "../ui/Section";

export const DesignerPromo = () => {
  const t = useTranslations("DesignerPromo");

  return (
    <Section id="designer-promo" noPadding contentClassName="py-20 px-6">
      <div className="relative p-8 md:p-12 rounded-3xl overflow-hidden border border-(--border-subtle) bg-(--bg-card)/50 backdrop-blur-sm shadow-sm group hover:shadow-md transition-shadow duration-500">
        {/* Subtle decorative background gradient */}
        <div className="hidden absolute top-0 right-0 w-64 h-64 bg-(--brand-primary)/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />

        {/* Ambient Light – orbiting inside the card */}
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <motion.div
            animate={{
              // very slow, irregular circular-ish path
              x: ["-40%", "40%", "40%", "-40%", "-40%", "10%", "-10%", "-40%"],
              y: ["-40%", "-40%", "40%", "40%", "-40%", "-10%", "10%", "-40%"],
              opacity: [
                "calc(0.05 + var(--ambient-hover))",
                "calc(0.12 + var(--ambient-hover))",
                "calc(0.08 + var(--ambient-hover))",
                "calc(0.1 + var(--ambient-hover))",
                "calc(0.06 + var(--ambient-hover))",
                "calc(0.1 + var(--ambient-hover))",
                "calc(0.07 + var(--ambient-hover))",
                "calc(0.05 + var(--ambient-hover))",
              ],
            }}
            transition={{
              x: {
                duration: 60,
                repeat: Infinity,
                ease: "easeInOut",
              },
              y: {
                duration: 60,
                repeat: Infinity,
                ease: "easeInOut",
              },
              opacity: {
                duration: 60,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
            // Fixed: Using CSS transition for interactions to avoid Framer variant/animate conflicts
            // group-hover:brightness-[1.02] adds subtle +2% brightness when card is hovered
            className="
  absolute
  top-1/2 left-1/2
  -translate-x-1/2 -translate-y-1/2
  w-[90%] h-[90%]
  rounded-full
  blur-[120px]
  filter
  transition-[filter] duration-500
  [--ambient-hover:0]
  group-hover:[--ambient-hover:0.015]
"
            style={{
              background:
                "radial-gradient(circle at center, var(--brand-primary) 0%, transparent 65%)",
            }}
          />
        </div>

        <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 md:gap-12 text-center md:text-left">
          {/* Icon Circle */}
          <div className="w-16 h-16 rounded-2xl bg-(--bg-primary) border border-(--border-subtle) flex items-center justify-center shrink-0 shadow-sm rotate-3 group-hover:rotate-6 transition-transform duration-500">
            <Palette className="w-8 h-8 text-(--brand-primary)" />
          </div>

          <div className="flex-1 space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold text-(--text-primary) font-outfit">
              {t("title")}
            </h2>
            <p className="text-(--text-secondary) leading-relaxed max-w-2xl">
              {t("description")}
            </p>
          </div>

          <div className="shrink-0">
            <a
              href="https://dribbble.com/Larussa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-(--brand-primary) text-white font-bold hover:bg-opacity-90 transition-all shadow-lg shadow-(--brand-primary)/25 group/btn"
            >
              <span>{t("cta")}</span>
              <ExternalLink className="w-4 h-4 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </Section>
  );
};
